import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import api from "../../lib/api";
import { ResultBadge } from "./Dashboard";
import { Search, CloudOff, AlertCircle } from "lucide-react";

export default function AttendancePage() {
    const [rows, setRows] = useState([]);
    const [buses, setBuses] = useState([]);
    const [filters, setFilters] = useState({
        bus_id: "",
        result: "",
        payment_status: "",
        date: "",
    });
    const [search, setSearch] = useState("");
    const [needsReviewOnly, setNeedsReviewOnly] = useState(false);

    const load = async () => {
        const params = Object.fromEntries(
            Object.entries(filters).filter(([, v]) => v !== "")
        );
        const { data } = await api.get("/attendance", { params });
        setRows(data);
    };

    useEffect(() => {
        api.get("/buses").then((r) => setBuses(r.data));
    }, []);

    useEffect(() => {
        load();
    }, [filters]);

    const visible = rows.filter((r) => {
        if (needsReviewOnly && !r.needs_review) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (r.student_name || "").toLowerCase().includes(q) ||
            (r.student_roll || "").toLowerCase().includes(q) ||
            (r.operator_name || "").toLowerCase().includes(q)
        );
    });

    const counts = {
        total: rows.length,
        allowed: rows.filter((r) => r.result === "allowed").length,
        blocked: rows.filter((r) => r.result === "blocked").length,
        needsReview: rows.filter((r) => r.needs_review).length,
    };

    return (
        <AdminLayout>
            <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]">
                <div className="mb-10">
                    <p className="label-caps mb-2">Audit</p>
                    <h1 className="font-display text-5xl text-[#111111]">Attendance Logs</h1>
                    <p className="text-[#666666] mt-2">
                        {counts.total} records · {counts.allowed} allowed · {counts.blocked} blocked
                        {counts.needsReview > 0 && (
                            <>
                                {" "}
                                ·{" "}
                                <button
                                    onClick={() => setNeedsReviewOnly((v) => !v)}
                                    data-testid="filter-needs-review"
                                    className={`font-bold ${
                                        needsReviewOnly ? "text-[#FF9500] underline" : "text-[#FF9500]"
                                    }`}
                                >
                                    {counts.needsReview} needs review
                                </button>
                            </>
                        )}
                    </p>
                </div>

                <div className="bg-white border border-[#E5E5E5] p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <select
                        value={filters.bus_id}
                        onChange={(e) => setFilters({ ...filters, bus_id: e.target.value })}
                        data-testid="filter-bus"
                        className="bg-[#F8F9FA] border border-[#E5E5E5] px-3 py-2 text-sm"
                    >
                        <option value="">All Buses</option>
                        {buses.map((b) => (
                            <option key={b.id} value={b.id}>
                                {b.bus_name}
                            </option>
                        ))}
                    </select>
                    <select
                        value={filters.result}
                        onChange={(e) => setFilters({ ...filters, result: e.target.value })}
                        data-testid="filter-result"
                        className="bg-[#F8F9FA] border border-[#E5E5E5] px-3 py-2 text-sm"
                    >
                        <option value="">All Results</option>
                        <option value="allowed">Allowed</option>
                        <option value="blocked">Blocked</option>
                        <option value="invalid">Invalid</option>
                    </select>
                    <select
                        value={filters.payment_status}
                        onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}
                        data-testid="filter-payment"
                        className="bg-[#F8F9FA] border border-[#E5E5E5] px-3 py-2 text-sm"
                    >
                        <option value="">All Payments</option>
                        <option value="paid">Paid</option>
                        <option value="due">Due</option>
                    </select>
                    <input
                        type="date"
                        value={filters.date}
                        onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                        data-testid="filter-date"
                        className="bg-[#F8F9FA] border border-[#E5E5E5] px-3 py-2 text-sm"
                    />
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" />
                        <input
                            type="text"
                            placeholder="Search student / operator…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="filter-search"
                            className="w-full bg-[#F8F9FA] border border-[#E5E5E5] pl-8 pr-3 py-2 text-sm"
                        />
                    </div>
                </div>

                <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F8F9FA] border-b-2 border-[#111111]">
                            <tr>
                                <th className="text-left font-bold py-3 px-4">Student</th>
                                <th className="text-left font-bold py-3 px-4">Roll</th>
                                <th className="text-left font-bold py-3 px-4">Bus</th>
                                <th className="text-left font-bold py-3 px-4">Route</th>
                                <th className="text-left font-bold py-3 px-4">Operator</th>
                                <th className="text-left font-bold py-3 px-4">Time</th>
                                <th className="text-left font-bold py-3 px-4">Payment</th>
                                <th className="text-left font-bold py-3 px-4">Result</th>
                            </tr>
                        </thead>
                        <tbody data-testid="attendance-table">
                            {visible.map((r) => (
                                <tr key={r.id} className="border-b border-[#E5E5E5] hover:bg-[#F8F9FA]">
                                    <td className="py-3 px-4 font-medium">{r.student_name}</td>
                                    <td className="py-3 px-4 font-mono text-xs text-[#666666]">
                                        {r.student_roll || "—"}
                                    </td>
                                    <td className="py-3 px-4">{r.bus_name}</td>
                                    <td className="py-3 px-4 text-[#666666]">{r.route_name}</td>
                                    <td className="py-3 px-4 text-[#666666]">{r.operator_name}</td>
                                    <td className="py-3 px-4 font-mono text-xs">
                                        {new Date(r.scan_time).toLocaleString()}
                                    </td>
                                    <td className="py-3 px-4 uppercase text-xs font-bold">
                                        {r.payment_status_at_scan || "—"}
                                    </td>
                                    <td className="py-3 px-4">
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <ResultBadge result={r.result} />
                                            {r.synced_offline && (
                                                <span
                                                    title="Recorded on-device while offline, synced later"
                                                    className="inline-flex items-center gap-1 bg-[#666666]/10 text-[#666666] border border-[#666666]/30 font-bold px-2 py-1 text-xs uppercase tracking-wider"
                                                >
                                                    <CloudOff size={11} /> Offline
                                                </span>
                                            )}
                                            {r.needs_review && (
                                                <span
                                                    title="QR wasn't in the operator's cached student list at scan time — allowed by offline fail-open policy"
                                                    className="inline-flex items-center gap-1 bg-[#FF9500]/10 text-[#FF9500] border border-[#FF9500]/30 font-bold px-2 py-1 text-xs uppercase tracking-wider"
                                                >
                                                    <AlertCircle size={11} /> Needs Review
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {visible.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="p-10 text-center text-[#666666]">
                                        No records match your filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminLayout>
    );
}
